import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { it } from "date-fns/locale";
import { Shirt, Plus, Trash2, Send, ArrowRight, FileDown, RefreshCw, Loader2, Eraser, CalendarDays, Factory } from "lucide-react";

const codKaze = (codiceGe) => {
  const up = (codiceGe || "").toUpperCase();
  if (up.startsWith("FANS")) return "KZMTEE001";
  if (up.startsWith("LMTD") || up.startsWith("SPCL")) return "KZMTEE011";
  return "—";
};

const splitDrop = (sku) => {
  const s = (sku || "").trim();
  const idx = s.lastIndexOf("-");
  if (idx === -1) return { codice_ge: s, taglia: "—" };
  return { codice_ge: s.slice(0, idx), taglia: s.slice(idx + 1) };
};

const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtIT = (d) => (d ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}` : "—");
const monthStart = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); };
const monthEnd = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth() + 1, 0); };

export default function GestioneDrop() {
  const [orderId, setOrderId] = useState("");
  const [righe, setRighe] = useState([{ uid: crypto.randomUUID(), sku_completo: "FANS26A-S", quantita: 2, nomi: "" }]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [range, setRange] = useState({ from: monthStart(), to: monthEnd() });
  const [closing, setClosing] = useState(false);

  const buildParams = () => {
    const params = {};
    if (range?.from) params.da = fmtISO(range.from);
    if (range?.to) params.a = fmtISO(range.to);
    return params;
  };

  const setRiga = (i, k, v) => setRighe((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRiga = () => setRighe((prev) => [...prev, { uid: crypto.randomUUID(), sku_completo: "", quantita: 1, nomi: "" }]);
  const delRiga = (i) => setRighe((prev) => prev.filter((_, idx) => idx !== i));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range?.from) params.da = fmtISO(range.from);
      if (range?.to) params.a = fmtISO(range.to);
      const { data } = await api.get("/drop/aggregato", { params });
      setRows(data);
    } catch (e) {
      toast.error("Errore nel caricamento della coda Drop");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const valid = righe.filter((r) => r.sku_completo.trim());
    if (valid.length === 0) { toast.error("Aggiungi almeno una riga valida"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/drop/simula", {
        id_ordine_shopify: orderId || null,
        righe: valid.map((r) => ({
          sku_completo: r.sku_completo.trim(),
          quantita: Number(r.quantita) || 1,
          nomi: r.nomi ? r.nomi.split(",").map((n) => n.trim()).filter(Boolean) : [],
        })),
      });
      toast.success(`Ordine Drop ${data.id_ordine_shopify} ricevuto`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella simulazione dell'ordine Drop");
    } finally {
      setSending(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/drop/pdf", { responseType: "blob", params: buildParams() });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "scheda_produzione_drop.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Scheda PDF generata");
    } catch (e) {
      toast.error("Errore durante la generazione del PDF");
    } finally {
      setExporting(false);
    }
  };

  const closeAndExport = async () => {
    if (rows.length === 0) { toast.error("Nessun ordine nel periodo selezionato"); return; }
    if (!window.confirm(`Chiudere ${rows.length} modelli del periodo ${fmtIT(range?.from)} - ${fmtIT(range?.to)} e inviarli in fabbrica?`)) return;
    setClosing(true);
    try {
      await exportPdf();
      const { data } = await api.post("/drop/chiudi", null, { params: buildParams() });
      toast.success(`${data.aggiornati} ordini inviati a fabbrica`);
      load();
    } catch (e) {
      toast.error("Errore durante la chiusura degli ordini");
    } finally {
      setClosing(false);
    }
  };

  const svuota = async () => {
    if (!window.confirm("Svuotare tutta la coda di produzione Drop?")) return;
    try {
      await api.delete("/drop/svuota");
      toast.success("Coda Drop svuotata");
      load();
    } catch (e) {
      toast.error("Errore durante lo svuotamento");
    }
  };

  const totCapi = rows.reduce((s, r) => s + r.quantita_totale, 0);

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-fuchsia-400 text-xs uppercase tracking-widest mb-1">
            <Shirt className="w-4 h-4" /> Produzione Drop · Solo T-shirt
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 tracking-tight">Gestione Drop</h1>
          <p className="text-slate-400 text-sm mt-1">SKU semplificato «CODICE-TAGLIA» (es. FANS26A-S). Raggruppato per Grafica e Taglia.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="drop-refresh-btn" onClick={load} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700/40 text-sm">
            <RefreshCw className="w-4 h-4" /> Aggiorna
          </button>
          <button data-testid="drop-clear-btn" onClick={svuota} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm">
            <Eraser className="w-4 h-4" /> Svuota
          </button>
          <button data-testid="drop-pdf-btn" onClick={exportPdf} disabled={exporting}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold text-sm disabled:opacity-60">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Genera Scheda PDF
          </button>
        </div>
      </div>

      {/* Filtro periodo (Calendario) */}
      <div className="glass rounded-xl p-5 mb-5" data-testid="drop-period-filter">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-4 h-4 text-fuchsia-400" />
          <h3 className="font-heading text-lg font-semibold text-slate-100">Seleziona Periodo Drop</h3>
        </div>
        <div className="flex flex-col md:flex-row gap-4 md:items-end">
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="drop-daterange-trigger" className="flex-1 grid grid-cols-2 gap-3 text-left group">
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Data Inizio</div>
                  <div data-testid="drop-data-inizio" className="bg-slate-900/60 border border-slate-700 group-hover:border-fuchsia-400/60 rounded-lg px-3 py-2.5 text-slate-100 text-sm font-mono-tech transition-colors">{fmtIT(range?.from)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Data Fine</div>
                  <div data-testid="drop-data-fine" className="bg-slate-900/60 border border-slate-700 group-hover:border-fuchsia-400/60 rounded-lg px-3 py-2.5 text-slate-100 text-sm font-mono-tech transition-colors">{fmtIT(range?.to)}</div>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0 bg-slate-900 border-slate-700" data-testid="drop-calendar-popover">
              <Calendar mode="range" numberOfMonths={2} locale={it} weekStartsOn={1}
                selected={range} onSelect={(r) => setRange(r || {})} defaultMonth={range?.from} />
              <div className="flex justify-between items-center gap-2 p-3 border-t border-slate-700">
                <button data-testid="drop-preset-month" onClick={() => setRange({ from: monthStart(), to: monthEnd() })} className="text-xs text-fuchsia-300 hover:text-fuchsia-200 font-medium">Mese corrente</button>
                <span className="text-xs text-slate-500">Seleziona un intervallo di date</span>
              </div>
            </PopoverContent>
          </Popover>
          <button data-testid="drop-close-factory-btn" onClick={closeAndExport} disabled={closing || exporting}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-lg px-5 py-3 flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap">
            {closing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Factory className="w-5 h-5" />} CHIUDI ORDINI E GENERA PDF FABBRICA
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-3">Vengono inclusi solo gli ordini con data compresa tra Data Inizio e Data Fine (estremi inclusi). La chiusura marca gli ordini del periodo come «Inviato a Fabbrica».</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Generatore ordine drop */}
        <div className="glass rounded-xl p-5">
          <h3 className="font-heading text-lg font-semibold text-slate-100 mb-4">Nuovo Ordine Drop</h3>
          <label className="text-xs text-slate-400 uppercase tracking-wider">ID Ordine Shopify (opzionale)</label>
          <input data-testid="drop-order-id" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Auto-generato se vuoto"
            className="mt-1 mb-4 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-fuchsia-400/60 text-sm" />

          <div className="space-y-3">
            {righe.map((r, i) => {
              const p = splitDrop(r.sku_completo);
              return (
                <div key={r.uid} className="bg-slate-900/40 border border-slate-700/60 rounded-lg p-3" data-testid={`drop-line-${i}`}>
                  <div className="flex gap-2 items-start">
                    <input value={r.sku_completo} onChange={(e) => setRiga(i, "sku_completo", e.target.value)} placeholder="SKU (es. FANS26A-S)" data-testid={`drop-sku-${i}`}
                      className="flex-1 bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2 text-slate-100 font-mono-tech text-sm focus:outline-none focus:border-fuchsia-400/60" />
                    <input type="number" min="1" value={r.quantita} onChange={(e) => setRiga(i, "quantita", e.target.value)} data-testid={`drop-qty-${i}`}
                      className="w-20 bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-fuchsia-400/60" />
                    <button onClick={() => delRiga(i)} className="w-9 h-9 rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/25 flex items-center justify-center shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <input value={r.nomi} onChange={(e) => setRiga(i, "nomi", e.target.value)} placeholder="Nomi retro maglia (separati da virgola, opzionale)" data-testid={`drop-nomi-${i}`}
                    className="mt-2 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-fuchsia-400/60" />
                  {r.sku_completo.trim() && (
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                      <span className="text-slate-500">Split:</span>
                      <span className="font-mono-tech text-fuchsia-300 bg-fuchsia-400/10 px-1.5 py-0.5 rounded">Grafica: {p.codice_ge}</span>
                      <ArrowRight className="w-3 h-3 text-slate-600" />
                      <span className="font-mono-tech text-cyan-300 bg-cyan-400/10 px-1.5 py-0.5 rounded">Taglia: {p.taglia}</span>
                      <span className="font-mono-tech text-amber-300 bg-amber-400/10 px-1.5 py-0.5 rounded">Cod. Kaze: {codKaze(p.codice_ge)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button onClick={addRiga} data-testid="drop-add-line-btn" className="mt-3 flex items-center gap-2 text-sm text-fuchsia-300 hover:text-fuchsia-200">
            <Plus className="w-4 h-4" /> Aggiungi riga
          </button>

          <button data-testid="drop-simulate-btn" onClick={send} disabled={sending}
            className="mt-5 w-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Invia Ordine Drop
          </button>

          <div className="mt-4 text-xs text-slate-500 bg-slate-900/40 rounded-lg p-3 border border-slate-700/60">
            <div className="font-semibold text-slate-400 mb-1">Endpoint Webhook Drop (Shopify)</div>
            <code className="font-mono-tech text-fuchsia-400 break-all">POST {process.env.REACT_APP_BACKEND_URL}/api/drop/shopify/webhook</code>
          </div>
        </div>

        {/* Coda produzione drop */}
        <div className="glass rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-slate-100">Coda Produzione Drop</h3>
            <span className="text-xs text-slate-400">Totale capi: <strong className="text-slate-100 font-mono-tech">{totCapi}</strong></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="drop-table">
              <thead>
                <tr className="border-b border-slate-700/60">
                  {["Grafica", "Taglia", "Quantità", "Nomi Retro Maglia"].map((h) => (
                    <th key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-3 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-12 text-center text-slate-500">Nessun ordine Drop. Invia un ordine per iniziare.</td></tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={`${r.codice_ge}-${r.taglia}`} data-testid={`drop-row-${i}`} className="border-b border-slate-800/60 hover:bg-slate-700/20 transition-colors">
                      <td className="px-3 py-2.5">
                        <span className="font-mono-tech text-fuchsia-300 bg-fuchsia-400/10 px-2 py-0.5 rounded text-sm">{r.codice_ge || "—"}</span>
                      </td>
                      <td className="px-3 py-2.5 font-mono-tech text-slate-300 text-sm">{r.taglia || "—"}</td>
                      <td className="px-3 py-2.5">
                        <span className="font-mono-tech text-lg font-bold text-slate-100">{r.quantita_totale}</span>
                        <span className="text-slate-500 text-xs ml-1">pz</span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-300 text-sm">{(r.nomi && r.nomi.length) ? r.nomi.join(", ") : <span className="text-slate-600">—</span>}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
